import { DataService } from "../utils/dataService";
import { path } from "../config/enviroment";

export type MarcaBlancaVerifyParams = {
  clientId: string;
  documentNumber: string;
  documentTypeId: string | number;
  hash?: string;
};

export type MarcaBlancaVerifyResult = {
  // true si el backend respondió finalResult === "Success".
  ok: boolean;
  // true si hubo un error de red (no se pudo determinar el resultado).
  errored: boolean;
  // true si el backend indica que el hash enviado es inválido o expiró.
  hashInvalid: boolean;
};

// El backend reporta el hash inválido/expirado con un textResponse fijo en
// inglés ("The provided hash is invalid or has expired"). Hacemos un match
// laxo (hash + inválido/expirado) para tolerar pequeñas variaciones de copy.
const isInvalidHashText = (text?: string): boolean => {
  if (!text) return false;
  const t = text.toLowerCase();
  return t.includes("hash") && (t.includes("invalid") || t.includes("expired"));
};

/**
 * Llama al endpoint `/v2/white-mark/lookup` (source: "conex"). Este endpoint
 * crea/persiste la validación Marca Blanca y devuelve su estado actual en la
 * misma llamada. Se usa en dos momentos:
 *  - al validar en el modal, para guardar la validación en el backend;
 *  - en los chokepoints, para re-verificar el acceso.
 *
 * Devuelve `ok` (finalResult === "Success"), `errored` (hubo error de red) y
 * `hashInvalid` (el hash enviado es inválido/expirado). El guardia solo bloquea
 * ante un rechazo explícito del backend, no ante un error de red.
 */
const useMarcaBlancaVerify = () => {
  const verify = async ({
    clientId,
    documentNumber,
    documentTypeId,
    hash,
  }: MarcaBlancaVerifyParams): Promise<MarcaBlancaVerifyResult> => {
    try {
      const _dataService = new DataService();
      const res = await _dataService.post("/v2/white-mark/lookup", {
        source: "conex",
        epaycoId: Number(clientId),
        documentNumber,
        documentTypeId: Number(documentTypeId),
        url: path,
        ...(hash ? { hash } : {}),
      });
      return {
        ok: res?.data?.data?.finalResult === "Success",
        errored: false,
        hashInvalid: isInvalidHashText(res?.data?.textResponse),
      };
    } catch (error) {
      const body = error?.response?.data;
      return {
        ok: false,
        errored: true,
        hashInvalid: isInvalidHashText(body?.textResponse),
      };
    }
  };

  return { verify };
};

export default useMarcaBlancaVerify;
